/**
 * Labstep
 *
 * @module core/OneDrivePicker
 * @desc OneDrivePicker component
 */

import { FileActionCreatePicker as Picker } from 'labstep-web/components/File/Action/Create/Picker';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import bugsnagService from 'labstep-web/services/bugsnag.service';
import { configService } from 'labstep-web/services/config.service';
import React from 'react';
import type {
  APIResponseBody,
  IOneDrivePickerFileContainerProps,
  IOneDrivePickerProps,
  OneDriveErrorType,
  OneDriveSuccessResponseType,
} from './types';

export class OneDrivePicker extends React.Component<IOneDrivePickerProps> {
  public static defaultProps = {
    multiple: false,
  };

  public constructor(props: IOneDrivePickerProps) {
    super(props);
    this.onClick = this.onClick.bind(this);
  }

  public onClick(): void {
    const { create, options = {}, onSelect, multiple } = this.props;

    const onSuccess = (body: APIResponseBody): void => {
      create(body as any, options);
    };

    const oneDriveOptions = {
      clientId: configService.oneDriveClientId,
      action: 'share',
      multiSelect: multiple,
      advanced: {
        redirectUri: window.location.origin,
      },
      error(error: OneDriveErrorType): void {
        console.error('error=', error);
        bugsnagService.notify(error);
      },
      success: (response: OneDriveSuccessResponseType): void => {
        if (!response.value) {
          bugsnagService.notify('OneDrive response failed', {
            response,
          });
          return;
        }
        const filesToUpload = response.value.map((oneDriveFile) => {
          return {
            path: oneDriveFile.permissions[0].link.webUrl,
            size: oneDriveFile.size,
            name: oneDriveFile.name,
            mime_type:
              oneDriveFile.file?.mimeType ||
              'application/octet-stream',
            link_source: 'onedrive',
          } as APIResponseBody;
        });

        if (onSelect) {
          onSelect(filesToUpload);
        }

        filesToUpload.forEach((file) => {
          onSuccess(file);
        });
      },
    };
    window.OneDrive.open(oneDriveOptions);
  }

  public render(): React.ReactElement {
    return (
      <Picker onClick={this.onClick} icon="cloud" text="One Drive" />
    );
  }
}

export const OneDrivePickerFileContainer: React.FC<
  IOneDrivePickerFileContainerProps
> = ({ parentName, parentId, ...rest }) => (
  <EntityCreateContainer
    entityName="file"
    parentName={parentName}
    parentId={parentId}
  >
    {({ create }): React.ReactElement => (
      <OneDrivePicker create={create} {...rest} />
    )}
  </EntityCreateContainer>
);
